import React, { useCallback } from 'react';
import Modal from 'components/Modal';
import TextButton from 'components/TextButton';
import { useSelector } from 'react-redux';
import { useModal } from 'lib/hooks';
import { useDataLayer } from 'hooks/useDataLayer';
import { Box, Flex, Icon, Text } from '@qga/roo-ui/components';
import camelCase from 'lodash/camelCase';
import MarkdownToJsx from 'markdown-to-jsx';
import { getPropertyName } from 'store/property/propertySelectors';

interface OfferTermsProps {
  offer: {
    name: string;
    offerDescription: string;
    highlights: {
      inclusions: {
        code: string;
        name: string;
        subtitle?: string;
      }[];
    };
    terms: {
      hotelTerms: string;
    };
  };
}

const OfferTerms = ({ offer }: OfferTermsProps) => {
  const { openModal, closeModal, modalProps } = useModal();
  const { emitInteractionEvent } = useDataLayer();
  const propertyName = useSelector(getPropertyName);

  const openModalHandler = useCallback(() => {
    openModal();
    emitInteractionEvent({ type: 'View Room Details Pop Up', value: 'Link Selected' });
  }, [emitInteractionEvent, openModal]);

  const closeModalHandler = useCallback(() => {
    closeModal();
    emitInteractionEvent({ type: 'View Room Details Pop Up', value: 'Dismiss Selected' });
  }, [emitInteractionEvent, closeModal]);

  return (
    <>
      <TextButton onClick={openModalHandler} fontSize="base" color="greys.charcoal" aria-label="View offer terms">
        View offer terms
      </TextButton>
      <Modal
        {...modalProps}
        width="700"
        padding={2}
        onRequestClose={closeModalHandler}
        title="Terms & Conditions"
        fullScreen={false}
        footerComponent={() => <></>}
      >
        <Flex flexDirection="column" p={4} justifyContent="space-between" alignItems="flex-start">
          <Box fontSize="lg" mb={2}>
            {propertyName}
          </Box>
          <Box fontSize="base" mb={10}>
            {offer.offerDescription}
          </Box>
          <Flex mb={3} flexDirection="column">
            <Text fontSize="base" mb={2}>
              Exclusive inclusions:
            </Text>
            {offer.highlights.inclusions?.map((inclusion, i) => (
              <Flex key={i} mb={2} data-testid="offer-inclusion">
                <Icon name={camelCase(inclusion.code)} color="greys.steel" size={20} mt={1} mr={2} />
                <Flex flexDirection="column">
                  <Text fontSize="base">{inclusion.name}</Text>
                  <Text fontSize="sm">{inclusion.subtitle}</Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
          <MarkdownToJsx>{offer.terms.hotelTerms}</MarkdownToJsx>
        </Flex>
      </Modal>
    </>
  );
};

export default OfferTerms;
